import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import InstallPrompt from "./components/InstallPrompt";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AvatarProvider } from "./context/AvatarContext";
import { SoundPlayerProvider } from "./context/SoundPlayerContext";

// Pages
import { Login, Register } from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Workouts from "./pages/Workouts";
import WorkoutSession from "./pages/WorkoutSession";
import TimerSession from "./pages/TimerSession";
import Sessions from "./pages/Sessions";
import Recovery from "./pages/Recovery";
import Performance from "./pages/Performance";
import Wearables from "./pages/Wearables";
import CoachDashboard from "./pages/CoachDashboard";
import CoachPortal from "./pages/CoachPortal";
import Profile from "./pages/Profile";
import CoachProfileSetup from "./pages/CoachProfileSetup";
import CoachAthleteView from "./pages/CoachAthleteView";
import LiveSessionsList from "./pages/LiveSessionsList";
import LiveSession from "./pages/LiveSession";
import Chat from "./pages/Chat";
import Nutrition from "./pages/Nutrition";
import Assessment from "./pages/Assessment";
import Mobility from "./pages/Mobility";
import Analyze from "./pages/Analyze";
import AnalyzeReportPage from "./pages/AnalyzeReportPage";
import Mental from "./pages/Mental";
import Cycle from "./pages/Cycle";
import Journey from "./pages/Journey";
import WellnessIQ from "./pages/WellnessIQ";
import World from "./pages/World";
import Village from "./pages/Village";
import DistrictView from "./pages/DistrictView";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Public Route (redirect to chat if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Role-aware dashboard
const RoleDashboard = () => {
  const { user } = useAuth();
  if (user?.role === 'coach') return <CoachDashboard />;
  return <Dashboard />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes — Chat is the new landing for everyone */}
      <Route path="/" element={<Chat />} />
      <Route path="/chat" element={<Chat />} />
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <RoleDashboard />
        </ProtectedRoute>
      } />
      <Route path="/workouts" element={
        <ProtectedRoute>
          <Workouts />
        </ProtectedRoute>
      } />
      <Route path="/sessions" element={
        <ProtectedRoute>
          <Sessions />
        </ProtectedRoute>
      } />
      <Route path="/sessions/:sessionId" element={
        <ProtectedRoute>
          <WorkoutSession />
        </ProtectedRoute>
      } />
      <Route path="/timer/:sessionId" element={
        <ProtectedRoute>
          <TimerSession />
        </ProtectedRoute>
      } />
      <Route path="/recovery" element={
        <ProtectedRoute>
          <Recovery />
        </ProtectedRoute>
      } />
      <Route path="/performance" element={
        <ProtectedRoute>
          <Performance />
        </ProtectedRoute>
      } />
      <Route path="/wearables" element={
        <ProtectedRoute>
          <Wearables />
        </ProtectedRoute>
      } />
      <Route path="/nutrition" element={
        <ProtectedRoute>
          <Nutrition />
        </ProtectedRoute>
      } />
      <Route path="/assessment" element={
        <ProtectedRoute>
          <Assessment />
        </ProtectedRoute>
      } />
      <Route path="/mobility" element={
        <ProtectedRoute>
          <Mobility />
        </ProtectedRoute>
      } />
      <Route path="/mental" element={
        <ProtectedRoute>
          <Mental />
        </ProtectedRoute>
      } />
      <Route path="/cycle" element={
        <ProtectedRoute>
          <Cycle />
        </ProtectedRoute>
      } />
      <Route path="/journey" element={
        <ProtectedRoute>
          <Journey />
        </ProtectedRoute>
      } />
      <Route path="/wellness-iq" element={
        <ProtectedRoute>
          <WellnessIQ />
        </ProtectedRoute>
      } />
      <Route path="/world/:pillar" element={
        <ProtectedRoute>
          <World />
        </ProtectedRoute>
      } />
      <Route path="/village/:pillar" element={
        <ProtectedRoute>
          <Village />
        </ProtectedRoute>
      } />
      <Route path="/district/:pillar" element={
        <ProtectedRoute>
          <DistrictView />
        </ProtectedRoute>
      } />
      <Route path="/analyze" element={
        <ProtectedRoute>
          <Analyze />
        </ProtectedRoute>
      } />
      <Route path="/analyze/:id" element={
        <ProtectedRoute>
          <AnalyzeReportPage />
        </ProtectedRoute>
      } />
      <Route path="/coach" element={
        <ProtectedRoute>
          <CoachPortal />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/coach/profile-setup" element={
        <ProtectedRoute>
          <CoachProfileSetup />
        </ProtectedRoute>
      } />
      <Route path="/coach/athlete/:athleteId" element={
        <ProtectedRoute>
          <CoachAthleteView />
        </ProtectedRoute>
      } />
      <Route path="/live-sessions" element={
        <ProtectedRoute>
          <LiveSessionsList />
        </ProtectedRoute>
      } />
      <Route path="/live-session/:sessionId" element={
        <ProtectedRoute>
          <LiveSession />
        </ProtectedRoute>
      } />

      {/* Catch all - redirect to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AvatarProvider>
          <SoundPlayerProvider>
            <div className="App dark">
              <AppRoutes />
              <Toaster position="top-right" richColors />
              <InstallPrompt />
            </div>
          </SoundPlayerProvider>
        </AvatarProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
